import React, { useState, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { RoomCard } from './RoomCard';
import { RoomDetailsModal } from './RoomDetailsModal';
import { EnhanceYourStay } from './EnhanceYourStay';
import { FormSection, FormSectionHeader, FormSectionContent } from '../BookingFormLayout';
import { getOfferingPrice, getOfferingCapacity, formatPrice } from '../../../state/offerings';
import { useBrandTheme } from '../brand-theme';
import type { TentConfigV4, TentType, RoomSelectionMessages } from '../types';
import type { NivaasPackage } from '../../../types/package';
import { BuyerType, getRateCardEntriesForOffering } from '../../../utils/price-resolution';

interface RoomSelectionStepProps {
  tents: TentConfigV4[];
  roomCounts: Map<TentType, number>;
  onQuantityChange: (type: TentType, delta: number) => void;
  availableTentTypes: TentType[];
  messages: RoomSelectionMessages;
  /** All available packages */
  packages?: NivaasPackage[];
  /** Selected package with rate card pricing */
  selectedPackage?: NivaasPackage | null;
  /** Callback when package selection changes */
  onSelectPackage?: (pkg: NivaasPackage | null) => void;
  /** Total guest count for occupancy calculation */
  totalGuestCount?: number;
  /** Whether this workspace supports occupancy-based pricing */
  supportsOccupancyPricing?: boolean;
  /** Mapping of offering slugs to offering IDs (for rate card lookup) */
  offeringSlugToId?: Map<string, string>;
}

/**
 * Room selection step container.
 * Displays large visual room cards with quantity selectors.
 */
export const RoomSelectionStep: React.FC<RoomSelectionStepProps> = ({
  tents,
  roomCounts,
  onQuantityChange,
  availableTentTypes,
  messages,
  packages = [],
  selectedPackage,
  onSelectPackage,
  totalGuestCount = 0,
  supportsOccupancyPricing = false,
  offeringSlugToId,
}) => {
  const { theme } = useBrandTheme();
  const [modalTent, setModalTent] = useState<TentConfigV4 | null>(null);

  const handleViewDetails = useCallback((tent: TentConfigV4) => {
    setModalTent(tent);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalTent(null);
  }, []);

  // Calculate total rooms selected
  const totalRoomsSelected = Array.from(roomCounts.values()).reduce((sum, count) => sum + count, 0);

  // Get offering IDs for selected rooms (for package filtering)
  const selectedOfferingIds = useMemo(() => {
    const ids: string[] = [];
    for (const [tentType, count] of roomCounts.entries()) {
      if (count > 0 && offeringSlugToId) {
        const offeringId = offeringSlugToId.get(tentType);
        if (offeringId) {
          ids.push(offeringId);
        }
      }
    }
    return ids;
  }, [roomCounts, offeringSlugToId]);

  // Calculate auto-detected occupancy based on guest count
  const autoOccupancy = useMemo(() => {
    if (totalRoomsSelected === 0) return 2; // Default to double
    const guestsPerRoom = Math.ceil(totalGuestCount / totalRoomsSelected);
    return Math.min(Math.max(guestsPerRoom, 1), 2);
  }, [totalGuestCount, totalRoomsSelected]);

  // Get rate card prices for an offering
  // Returns prices based on workspace's occupancy pricing support
  // Prices are returned as PER-PERSON amounts from the rate card
  const getRateCardPrices = useCallback(
    (tentType: TentType): { single?: number; double?: number; flat?: number; offeringCapacity: number } | null => {
      if (!selectedPackage?.rateCard || !offeringSlugToId) return null;
      
      const offeringId = offeringSlugToId.get(tentType);
      if (!offeringId) return null;
      
      // Get the offering's actual capacity from offerings state
      const offeringCapacity = getOfferingCapacity(tentType) ?? 2;
      
      const entries = getRateCardEntriesForOffering(selectedPackage, offeringId, BuyerType.PUBLIC);
      if (entries.length === 0) return null;
      
      // For workspaces with occupancy pricing, look for single/double entries
      const singleEntry = entries.find((e) => e.occupancy === 1);
      const doubleEntry = entries.find((e) => e.occupancy === 2);
      
      // For workspaces without occupancy pricing, look for full capacity entry
      // The rate card stores entries with occupancy = offering capacity
      const flatEntry = entries.find((e) => e.occupancy === offeringCapacity);
      
      return {
        single: singleEntry?.unitPrice.amount,
        double: doubleEntry?.unitPrice.amount,
        flat: flatEntry?.unitPrice.amount,
        offeringCapacity, // Return the offering's capacity for display calculations
      };
    },
    [selectedPackage, offeringSlugToId],
  );

  return (
    <FormSection>
      <FormSectionHeader
        title={messages.title}
        subtitle={messages.subtitle}
        action={
          totalRoomsSelected > 0 ? (
            <span className={clsx('text-sm font-serif font-medium px-3 py-1 rounded-md', theme.accentText, theme.accentBgLight)}>
              {totalRoomsSelected} {messages.roomsSelected}
            </span>
          ) : null
        }
      />
      <FormSectionContent>
        {/* Enhance Your Stay - Package selection shown first */}
        {packages.length > 0 && onSelectPackage && (
          <EnhanceYourStay
            packages={packages}
            selectedOfferingIds={selectedOfferingIds}
            selectedPackage={selectedPackage ?? null}
            onSelectPackage={onSelectPackage}
            messages={{
              title: messages.enhanceYourStayTitle,
              subtitle: messages.enhanceYourStaySubtitle,
            }}
          />
        )}

        {/* Room cards grid */}
        <div
          className={clsx(
            'grid gap-4 md:gap-6',
            // 1 column on mobile, 2 on tablet/desktop
            'grid-cols-1 md:grid-cols-2',
          )}
        >
          {tents.map((tent) => {
            const isAvailable = availableTentTypes.includes(tent.tentType);
            const quantity = roomCounts.get(tent.tentType) || 0;
            const legacyPrice = getOfferingPrice(tent.tentType) || 0;
            
            // Always try to get rate card prices from the selected package
            const rateCardPrices = getRateCardPrices(tent.tentType);
            
            // Get offering capacity from offerings state (more reliable than tent.capacity)
            const offeringCapacity = rateCardPrices?.offeringCapacity ?? tent.capacity ?? 2;
            
            // Determine price based on workspace settings and rate card availability
            // Rate card stores price per person per night, so we multiply by capacity for display
            let pricePerNight: number;
            if (rateCardPrices) {
              if (supportsOccupancyPricing && (rateCardPrices.single || rateCardPrices.double)) {
                // Workspace supports occupancy pricing - use single/double based on auto-detected occupancy
                // Price is per person, multiply by occupancy for per-room display
                const perPersonPrice = (autoOccupancy === 1 ? rateCardPrices.single : rateCardPrices.double) ?? 0;
                pricePerNight = perPersonPrice > 0 ? perPersonPrice * autoOccupancy : legacyPrice;
              } else if (rateCardPrices.flat) {
                // Workspace doesn't support occupancy pricing - flat rate is per person at full capacity
                // Multiply by OFFERING CAPACITY (not occupancy) for per-room display
                pricePerNight = rateCardPrices.flat * offeringCapacity;
              } else {
                // Fallback to legacy price (already capacity-adjusted)
                pricePerNight = legacyPrice;
              }
            } else {
              // No rate card, use legacy price (already capacity-adjusted)
              pricePerNight = legacyPrice;
            }

            return (
              <div key={tent.tentType} className="flex flex-col gap-2">
                <RoomCard
                  tentConfig={tent}
                  quantity={quantity}
                  pricePerNight={pricePerNight}
                  isAvailable={isAvailable}
                  onQuantityChange={(delta) => onQuantityChange(tent.tentType, delta)}
                  onViewDetails={() => handleViewDetails(tent)}
                  messages={{
                    pricePerNight: messages.pricePerNight,
                    viewDetails: messages.viewDetails,
                    capacity: messages.capacity,
                    mealsIncluded: messages.mealsIncluded,
                    privateBathroom: messages.privateBathroom,
                    sharedBathroom: messages.sharedBathroom,
                    unavailable: messages.unavailable,
                  }}
                />
                
                {/* Price transparency for occupancy-based pricing - subtle inline display */}
                {supportsOccupancyPricing && rateCardPrices && (rateCardPrices.single || rateCardPrices.double) && offeringCapacity === 2 && (
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                    <span className="text-gray-400">|</span>
                    {rateCardPrices.single && (
                      <span className={clsx(
                        autoOccupancy === 1 ? 'text-gray-700 font-medium' : 'text-gray-400'
                      )}>
                        1 guest: {formatPrice({ amount: rateCardPrices.single, currency: 'INR' })}
                      </span>
                    )}
                    {rateCardPrices.single && rateCardPrices.double && (
                      <span className="text-gray-300">•</span>
                    )}
                    {rateCardPrices.double && (
                      <span className={clsx(
                        autoOccupancy === 2 ? 'text-gray-700 font-medium' : 'text-gray-400'
                      )}>
                        2 guests: {formatPrice({ amount: rateCardPrices.double * 2, currency: 'INR' })}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </FormSectionContent>

      {/* Room details modal */}
      <RoomDetailsModal
        isOpen={modalTent !== null}
        onClose={handleCloseModal}
        tentConfig={modalTent}
        pricePerNight={modalTent ? (getOfferingPrice(modalTent.tentType) || 0) : 0}
        messages={{
          close: 'Close',
          pricePerNight: messages.pricePerNight,
          amenities: 'Amenities',
          capacity: messages.capacity,
          privateBathroom: messages.privateBathroom,
          sharedBathroom: messages.sharedBathroom,
        }}
      />
    </FormSection>
  );
};

export default RoomSelectionStep;
